import * as THREE from "three";
import { EnemyAct, LevelSim } from "@highmarch/domain";
import { GroundMesh } from "../groundMesh";
import { roadLevelAt } from "../terrain";

/**
 * The ground telegraphs of the M21 enemies (docs/ENEMIES_V2.md), drawn from the sim's state every frame: a Summoner
 * casting stands in a violet rune circle that brightens as the cast runs out (its imps rise out of it), a Stunner
 * winding up stands in an amber warning disc the size of its shockwave whose fill sweeps out to the rim — red at the
 * end — so the player sees exactly which towers it will reach and when. Both run on the sim's clock (the act's ticks
 * left plus the tick fraction), so they hang with the sim during an offer and end on the tick the sim acts.
 */

/** Looks: the summoning circle's radius (cells), the colours, the fade after the act ends (s), the ground lift. */
export const RING_LOOK = {
  summonRadius: 0.62,
  summon: [0.88, 0.3, 1.0] as [number, number, number],
  stomp: [1.0, 0.62, 0.12] as [number, number, number],
  fadeOut: 0.28,
  lift: 0.045,
  rings: 7,
  segs: 56,
} as const;

const VERT = /* glsl */ `
attribute vec2 aR;    // x: radius 0..1 from the centre, y: round the disc 0..1
varying vec2 vR;
void main() { vR = aR; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

/** The Stunner's warning: a dashed rim, a filling front sweeping out with the wind-up, amber turning red. */
const STOMP_FRAG = /* glsl */ `
uniform float uP; uniform float uA; uniform float uT; uniform vec3 uC;
varying vec2 vR;
void main() {
  float r = vR.x, u = vR.y;
  float rim = smoothstep(0.86, 0.93, r) * smoothstep(1.0, 0.96, r);
  float dash = step(0.45, fract(u * 22.0 - uT * 0.8));
  float front = exp(-pow((r - uP) / 0.06, 2.0)) * step(0.02, uP);
  float fill = step(r, uP) * (0.16 + 0.12 * uP);
  float pulse = 0.82 + 0.18 * sin(uT * (8.0 + 22.0 * uP));
  vec3 c = mix(uC, vec3(1.0, 0.12, 0.04), uP * uP);
  float a = (rim * (0.5 + 0.5 * dash) + fill + front * 0.85) * uA * pulse;
  gl_FragColor = vec4(c * (1.0 + 1.6 * front + 0.6 * rim), clamp(a, 0.0, 1.0));
}`;

/** The Summoner's circle: two rims, turning runes between them, a pentagon inside, glowing up as the cast ends. */
const SUMMON_FRAG = /* glsl */ `
uniform float uP; uniform float uA; uniform float uT; uniform vec3 uC;
varying vec2 vR;
void main() {
  float r = vR.x, u = vR.y;
  float outer = exp(-pow((r - 0.92) / 0.045, 2.0));
  float inner = exp(-pow((r - 0.66) / 0.035, 2.0));
  float k = fract(u * 12.0 + uT * 0.18);
  float runes = step(0.28, k) * step(k, 0.66) * smoothstep(0.7, 0.74, r) * smoothstep(0.88, 0.84, r);
  float th = u * 6.28318 - uT * 0.5;
  float edge = r * cos(mod(th, 1.25664) - 0.62832);
  float star = exp(-pow((edge - 0.5) / 0.028, 2.0)) * step(r, 0.66);
  float glow = (1.0 - r) * 0.35 * uP;
  float m = clamp(outer + inner * 0.8 + runes * 0.9 + star * 0.75 + glow, 0.0, 1.0);
  vec3 c = mix(vec3(0.18, 0.03, 0.28), uC * (1.4 + 1.2 * uP), m);
  gl_FragColor = vec4(c, m * uA * (0.45 + 0.55 * uP));
}`;

/** A flat disc of `radius` round (x, z) laid over the ground (heights sampled per vertex), `aR` = (radius 0..1, angle 0..1). */
export function discGeometry(x: number, z: number, radius: number, heightAt: (x: number, z: number) => number): THREE.BufferGeometry {
  const pos: number[] = [], ar: number[] = [], idx: number[] = [];
  const R = RING_LOOK.rings, S = RING_LOOK.segs;
  for (let i = 0; i <= R; i++) {
    const rr = i / R;
    for (let j = 0; j <= S; j++) {
      const a = (j / S) * Math.PI * 2, px = x + Math.cos(a) * rr * radius, pz = z + Math.sin(a) * rr * radius;
      pos.push(px, heightAt(px, pz) + RING_LOOK.lift, pz);
      ar.push(rr, j / S);
    }
  }
  for (let i = 0; i < R; i++) for (let j = 0; j < S; j++) {
    const a = i * (S + 1) + j, b = a + S + 1;
    idx.push(a, b, a + 1, a + 1, b, b + 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("aR", new THREE.Float32BufferAttribute(ar, 2));
  g.setIndex(idx);
  return g;
}

/** A cast's or a wind-up's progress 0..1 on the sim clock: ticks gone of the total, plus this frame's tick fraction. Pure. */
export function actProgress(ticksLeft: number, total: number, alpha: number): number {
  if (total <= 0) return 1;
  return Math.max(0, Math.min(1, (total - ticksLeft + alpha) / total));
}

interface Ring { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; kind: "summon" | "stomp"; total: number; fade: number; seen: boolean }

export class AbilityRings {
  readonly group = new THREE.Group();
  private readonly rings = new Map<number, Ring>();
  private time = 0;

  constructor(private readonly sim: LevelSim, private readonly reduced: () => boolean) {}

  /** Per frame: a ring for every enemy casting or winding up now; one whose act ended fades out where it stood. */
  update(alpha: number, dt: number): void {
    this.time += dt;
    const sim = this.sim;
    for (const r of this.rings.values()) r.seen = false;
    for (const e of sim.enemySlots) {
      if (!e.alive || (e.act !== EnemyAct.Summoning && e.act !== EnemyAct.WindingUp)) continue;
      const k = sim.balance.enemies[e.archetype].abilities;
      const kind = e.act === EnemyAct.Summoning ? "summon" : "stomp";
      let r = this.rings.get(e.id);
      if (!r || r.kind !== kind || r.fade > 0) {
        if (r) this.drop(e.id, r);
        const x = e.x.toNumber(), z = e.y.toNumber();
        const radius = kind === "summon" ? RING_LOOK.summonRadius : k.stompRadius.toNumber();
        r = this.make(kind, x, z, radius, kind === "summon" ? Math.max(1, k.summonCastTicks) : Math.max(1, k.stompWindupTicks));
        this.rings.set(e.id, r);
      }
      r.seen = true;
      const u = r.mat.uniforms;
      u.uP.value = actProgress(e.actTicks, r.total, alpha);
      u.uA.value = 1;
    }
    for (const [id, r] of this.rings) {
      if (r.seen) continue;
      r.fade += dt;
      r.mat.uniforms.uA.value = Math.max(0, 1 - r.fade / RING_LOOK.fadeOut);
      if (r.fade >= RING_LOOK.fadeOut) this.drop(id, r);
    }
    const t = this.reduced() ? 0 : this.time;
    for (const r of this.rings.values()) r.mat.uniforms.uT.value = t;
  }

  private make(kind: "summon" | "stomp", x: number, z: number, radius: number, total: number): Ring {
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: kind === "summon" ? SUMMON_FRAG : STOMP_FRAG, transparent: true, depthWrite: false,
      uniforms: { uP: { value: 0 }, uA: { value: 1 }, uT: { value: 0 }, uC: { value: new THREE.Vector3(...(kind === "summon" ? RING_LOOK.summon : RING_LOOK.stomp)) } },
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4,
    });
    const mesh = new THREE.Mesh(discGeometry(x, z, radius, (px, pz) => Math.max(GroundMesh.drawnAt(px, pz), roadLevelAt(px, pz))), mat);
    mesh.renderOrder = 5;
    mesh.frustumCulled = false;
    this.group.add(mesh);
    return { mesh, mat, kind, total, fade: 0, seen: true };
  }

  private drop(id: number, r: Ring): void {
    this.group.remove(r.mesh);
    r.mesh.geometry.dispose(); r.mat.dispose();
    this.rings.delete(id);
  }

  dispose(): void {
    for (const [id, r] of [...this.rings]) this.drop(id, r);
    this.group.clear();
  }
}
